import { useEffect, useRef } from 'react';
import { hasObjectIdChanged } from 'helpers/idComparison';

export type ProjectChangeCallbacks = {
  onProjectChange: () => void;
  onProjectClear?: () => void;
};

/**
 * Hook that handles project change detection and triggers appropriate callbacks.
 * Use this in store initializers to ensure consistent behavior when user switches projects.
 *
 * @param projectId - Current project ID (null when no project selected)
 * @param callbacks - Functions to call on project change/clear
 *   - onProjectChange: Called when projectId changes from one value to another (reset + resync)
 *   - onProjectClear: Called when projectId becomes null (reset only). If not provided, onProjectChange is used.
 */
export function useProjectChangeEffect(
  projectId: string | null | undefined,
  callbacks: ProjectChangeCallbacks
): void {
  const previousProjectIdRef = useRef<string | null | undefined>(projectId);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    const previousProjectId = previousProjectIdRef.current;
    previousProjectIdRef.current = projectId;

    if (!projectId) {
      (callbacksRef.current.onProjectClear ?? callbacksRef.current.onProjectChange)();
      return;
    }

    if (hasObjectIdChanged(previousProjectId, projectId)) {
      callbacksRef.current.onProjectChange();
    }
  }, [projectId]);
}
